import { formatRupees } from "./money";

// The verification fee ledger (billing.service.ts, platform-admin.service.ts).
// A fee paid to a verifier earns its share; a refunded fee takes the share
// back as a negative REFUND entry. TickAndPass pays each verifier the
// balance and records the payout, which settles the entries it covered.

/** A REFUND entry takes a fee share back; its amount is negative. */
export const isRefund = (entry) => entry?.type === "REFUND";

/** "+ \u20B9125" for a share, "- \u20B9125" for a refund. */
export function ledgerAmountText(entry) {
  const sign = isRefund(entry) ? "-" : "+";
  return `${sign} ${formatRupees(Math.abs(entry?.amountPaise ?? 0))}`;
}

/** Where an entry stands with payouts, for the verifier's ledger. */
export function ledgerStatusText(entry, formatDate) {
  if (isRefund(entry)) {
    return entry.settledAt
      ? `Fee refunded, taken off the payout of ${formatDate(entry.settledAt)}`
      : "Fee refunded, comes off your next payout";
  }
  return entry.settledAt
    ? `Settled ${formatDate(entry.settledAt)}`
    : "Awaiting payout";
}

/**
 * What is waiting to be paid out, for the verifier's cards. More refunded
 * than is waiting means the difference comes off the next payout: that is
 * said, never shown as a negative amount. `awaitingText` describes a balance
 * that is waiting.
 */
export function payoutBalanceView(payablePaise, awaitingText) {
  if (payablePaise < 0) {
    return {
      value: formatRupees(-payablePaise),
      subtext: "Refunds to come off your next payout",
    };
  }
  return { value: formatRupees(payablePaise), subtext: awaitingText };
}

/**
 * The payouts still to make, one per verifier: every open entry of theirs
 * (shares and refunds), what they come to, and the entry ids a payout
 * record covers. Largest amount first.
 */
export function awaitingPayouts(entries) {
  const byVerifier = new Map();
  for (const entry of entries ?? []) {
    if (entry.isSettled) continue;
    const key = `${entry.verifierType}:${entry.verifierId}`;
    const group = byVerifier.get(key) ?? {
      key,
      verifierId: entry.verifierId,
      verifierType: entry.verifierType,
      verifierName: entry.verifierName,
      contact: entry.contactEmail || entry.regNumber || "",
      entryIds: [],
      feeCount: 0,
      refundCount: 0,
      earnedPaise: 0,
      refundedPaise: 0,
      firstEarnedAt: entry.createdAt,
    };
    group.entryIds.push(entry.id);
    if (isRefund(entry)) {
      group.refundCount += 1;
      group.refundedPaise -= entry.amountPaise;
    } else {
      group.feeCount += 1;
      group.earnedPaise += entry.amountPaise;
    }
    if (new Date(entry.createdAt) < new Date(group.firstEarnedAt)) {
      group.firstEarnedAt = entry.createdAt;
    }
    byVerifier.set(key, group);
  }
  return [...byVerifier.values()]
    .map((group) => ({
      ...group,
      netPaise: group.earnedPaise - group.refundedPaise,
    }))
    .sort((a, b) => b.netPaise - a.netPaise);
}

/** Today's date in India as YYYY-MM-DD, the default day a payout was made. */
export function todayInIndia(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}
